import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Lock, Zap } from 'lucide-react';
import { track } from '../utils/analytics';
import { useAppLanguage } from '../hooks/useAppLanguage';
import { useAppT } from '../hooks/useAppT';
import ConsentExecutionCheckbox from './ConsentExecutionCheckbox';
import { OVERAGE_EUR } from '../constants/plans';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Utility: pull the quota-exceeded payload out of an axios error. Returns
 * `null` for any error shape that isn't the backend's structured 402.
 * Use it in every analyze / upload / reanalyze catch:
 *
 *   const q = extractQuotaError(err);
 *   if (q) setQuotaData(q);
 *   else showGenericError(err);
 */
export function extractQuotaError(err) {
  const status = err && err.response && err.response.status;
  const detail = err && err.response && err.response.data && err.response.data.detail;
  if (status !== 402) return null;
  if (!detail || typeof detail !== 'object') return null;
  if (detail.error !== 'quota_exceeded') return null;
  return {
    unit: detail.unit || 'analysis',
    used: Number(detail.used) || 0,
    allowed: Number(detail.allowed) || 0,
    overage_price_eur: detail.overage_price_eur,
  };
}

/**
 * Same-shape helper for the tiered "you must upgrade to unlock this
 * feature" gate — used by Contract Guard today, but any future
 * paid-only surface can reuse it. Backend contract:
 *
 *   HTTP 402 { detail: { error: "upgrade_required", feature: "<slug>",
 *              message?: "<localised copy>" } }
 *
 * Returns null for any other error shape so the caller can fall
 * through to its generic message.
 */
export function extractUpgradeRequired(err) {
  const status = err && err.response && err.response.status;
  const detail = err && err.response && err.response.data && err.response.data.detail;
  if (status !== 402) return null;
  if (!detail || typeof detail !== 'object') return null;
  if (detail.error !== 'upgrade_required') return null;
  return {
    unit: 'upgrade_required',
    feature: detail.feature || 'archer',
    message: detail.message || '',
  };
}

/**
 * Pre-launch "checkout coming soon" gate. Backend contract (see
 * `credits_routes._price_id_for_tier`):
 *
 *   HTTP 503 { detail: { error: "checkout_not_configured",
 *              tier: "<tier>", message: "<localised copy>" } }
 *
 * Returns null for any other error shape so the caller falls through
 * to its generic error.
 */
export function extractCheckoutNotConfigured(err) {
  const status = err && err.response && err.response.status;
  const detail = err && err.response && err.response.data && err.response.data.detail;
  if (status !== 503) return null;
  if (!detail || typeof detail !== 'object') return null;
  if (detail.error !== 'checkout_not_configured') return null;
  return {
    unit: 'checkout_not_configured',
    tier: detail.tier || '',
    message: detail.message || '',
  };
}

/**
 * Jurist-case capacity gate. Backend contract (see
 * `routes/jurist_cases_routes.request_jurist_case`):
 *
 *   HTTP 409 { detail: { error: "jurist_quota_reached", active: N,
 *              allowed: M, overage_price_eur: 49,
 *              current_plan: "starter"|"pro" } }
 *
 * Returns null for any other error shape. The modal offers the €49
 * one-shot unlock and, on Starter, a secondary "Passer à Pro" CTA —
 * l'upsell vers Ultimate a disparu avec le palier.
 */
export function extractJuristQuotaReached(err) {
  const status = err && err.response && err.response.status;
  const detail = err && err.response && err.response.data && err.response.data.detail;
  if (status !== 409) return null;
  if (!detail || typeof detail !== 'object') return null;
  if (detail.error !== 'jurist_quota_reached') return null;
  return {
    unit: 'jurist_quota_reached',
    active: Number(detail.active) || 0,
    allowed: Number(detail.allowed) || 0,
    overage_price_eur: detail.overage_price_eur || OVERAGE_EUR.juristCase,
    current_plan: detail.current_plan || '',
  };
}

/**
 * Two-variant opt-in modal driven by a 402 quota_exceeded payload.
 *
 *   - unit === "analysis"          → offer a one-shot 3 € unlock (no
 *                                    expiry) or an upgrade to a higher plan.
 *   - unit === "upgrade_required"  → paid-only feature (Contract Guard).
 *                                    Single "Voir les plans" CTA, no
 *                                    à-la-carte purchase.
 *   - unit === "checkout_not_configured"
 *                                  → pre-launch "coming soon" screen
 *                                    for subscription checkouts before
 *                                    the Stripe prices are wired up.
 *                                    Single CTA routes back to the app.
 *
 * Never renders when `data` is null. Closes on ✕, on the "Voir les plans"
 * navigation, and on successful redirect to Stripe.
 */
// Le chemin où le client se trouve AU MOMENT de l'achat — c'est là qu'il
// doit revenir. On le lit plutôt que de le reconstruire depuis `caseId` :
// le même dossier a deux adresses (`/cases/:id` et `/cases/:id/analyse`,
// cf. les deux pages qui coexistent), et en choisir une reviendrait à
// déplacer le client sans le lui demander.
//
// La query est volontairement écartée : le serveur y ajoute la sienne
// (`?overage=success&…`), et rapporter l'ancienne ferait revenir un
// `?tab=` ou un `?adverse=1` périmé par-dessus.
function returnPath() {
  try {
    return window.location.pathname || '/dashboard';
  } catch (_) {
    return '/dashboard';
  }
}


export default function QuotaModal({ data, onClose, caseId = null }) {
  const navigate = useNavigate();
  // Trois des cinq variantes de cette modale (« bientôt disponible »,
  // capacité juriste, dépassement lettre) portaient déjà leurs trois
  // langues ; les deux autres (dépassement analyse, quota atteint) et
  // toute la copie « abonnement requis » étaient en français en dur — y
  // compris les messages d'erreur Stripe. Elles passent par le
  // dictionnaire comme le reste.
  const [lang] = useAppLanguage();
  const t = useAppT(lang);
  const [loading, setLoading] = useState(false);
  // Consentement à l'exécution immédiate (art. VI.53 CDE). Jamais
  // pré-coché, et remis à zéro à chaque ouverture de la modale : un
  // consentement donné pour un achat ne vaut pas pour le suivant.
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState(null);

  // Analytics — fire once whenever `data` transitions from null to a
  // payload (i.e. the modal actually becomes visible). React re-renders
  // don't refire the event because the dep is `data` itself, and the
  // component early-returns null when `data` is falsy.
  useEffect(() => {
    if (!data) return;
    track('quota_modal_shown', { unit: String(data.unit || 'unknown') });
    // La modale est montée en permanence par ses parents et pilotée par
    // `data` : sans cette remise à zéro, un client qui coche, ferme, puis
    // rouvre pour un AUTRE achat retrouverait la case déjà cochée. Ce
    // serait une case pré-cochée, avec un détour.
    setConsent(false);
  }, [data]);

  if (!data) return null;
  const isComingSoon = data.unit === 'checkout_not_configured';
  const isJuristQuota = !isComingSoon && data.unit === 'jurist_quota_reached';
  const isUpgrade = !isComingSoon && !isJuristQuota && data.unit === 'upgrade_required';
  const isAnalysis = !isUpgrade && !isComingSoon && !isJuristQuota && data.unit === 'analysis';
  const isLetter = !isUpgrade && !isComingSoon && !isJuristQuota && data.unit === 'letter';
  // Le 402 porte presque toujours `overage_price_eur` : ces valeurs sont
  // la garde « le backend a été laconique ». Elles viennent du catalogue
  // partagé, pas d'un chiffre retapé ici — c'est ce qui les empêche de
  // rester en arrière au prochain changement de grille.
  const priceEur = data.overage_price_eur || OVERAGE_EUR.analysis;
  const juristPriceEur = data.overage_price_eur || OVERAGE_EUR.juristCase;
  const activeCount = Number(data.active) || 0;
  // L'upsell secondaire vise le palier AU-DESSUS de celui du client, et
  // il n'y en a plus qu'un : Starter → Pro. Un client déjà Pro n'a plus
  // de palier à monter — il débloque un dossier, ou il en clôture un.
  const showProUpsell = isJuristQuota && (data.current_plan || '').toLowerCase() === 'starter';
  const allowedLabel = data.allowed === -1 ? '∞' : String(data.allowed);

  // Feature-specific copy for the upgrade variant. Falls back to a
  // generic label if the backend ships a `feature` slug we haven't
  // localised yet — nothing crashes, the CTA still routes to /plans.
  const FEATURE_COPY = {
    contract_guard: {
      eyebrow: t('quota.eyebrow_upgrade'),
      title: t('quota.feature_contract_guard_title'),
      body: (data && data.message) || t('quota.feature_contract_guard_body'),
    },
  };
  const featureCopy = FEATURE_COPY[data.feature] || (isUpgrade ? {
    eyebrow: t('quota.eyebrow_upgrade'),
    title: (data && data.message) || t('quota.feature_generic_title'),
    body: t('quota.feature_generic_body'),
  } : null);

  // Pre-launch waiting screen — one-line localised copy per language,
  // resolved off the same `archer_locale` bucket the rest of the home
  // uses. Body defaults to the FR string if the browser lang is
  // anything unusual, matching the current audience.
  const COMING_SOON_COPY = {
    fr: {
      eyebrow: 'BIENTÔT DISPONIBLE',
      title: 'Les abonnements ouvrent très bientôt.',
      body: "Archer est en tout dernier réglage avant le lancement. En attendant, tes deux analyses complètes du mois restent gratuites.",
      cta: 'Analyser un document — gratuit',
    },
    en: {
      eyebrow: 'COMING SOON',
      title: 'Subscriptions open very soon.',
      body: 'Archer is in the final polish before launch. In the meantime, your two full analyses a month stay free.',
      cta: 'Analyse a document — free',
    },
    nl: {
      eyebrow: 'BINNENKORT BESCHIKBAAR',
      title: 'Abonnementen komen zeer binnenkort.',
      body: 'Archer maakt de laatste puntjes op de i. Ondertussen blijven je twee volledige analyses per maand gratis.',
      cta: 'Een document analyseren — gratis',
    },
  };
  // `_cs_lang` relisait `archer_locale` à la main, une quatrième copie de
  // la même logique. C'est désormais `lang`, la langue déjà résolue et
  // réactive de l'app.
  const csCopy = COMING_SOON_COPY[lang] || COMING_SOON_COPY.fr;

  // Jurist-case capacity screen — same language resolution as the rest.
  // {N} is interpolated from `data.active` (how many files the jurist is
  // already handling for this user). Le CTA à 49 € est universel ; le
  // CTA secondaire « Passer à Pro » ne s'affiche qu'en Starter (1 slot).
  //
  // Le mot qui compte : « ton juriste travaille DÉJÀ sur … ». Un slot
  // n'est pas un quota épuisé qu'on attend de voir se recharger — c'est
  // quelqu'un qui est occupé. Clôturer un dossier le libère ; le 1er du
  // mois ne change rien.
  const JURIST_COPY = {
    fr: {
      eyebrow: 'CAPACITÉ JURISTE ATTEINTE',
      titleSingular: 'Ton juriste travaille déjà sur ton dossier.',
      titlePlural:   'Ton juriste travaille déjà sur {N} dossiers.',
      body: 'Un dossier se libère quand il est clos. Débloque-en un de plus à la demande, ou passe à Pro pour en mener deux de front.',
      unlockCta: 'Débloquer un dossier — {P}€',
      upsellCta: 'Passer à Pro',
    },
    en: {
      eyebrow: 'JURIST AT CAPACITY',
      titleSingular: 'Your jurist is already handling your case.',
      titlePlural:   'Your jurist is already handling {N} cases.',
      body: 'A case frees up when it closes. Unlock an extra one on demand, or upgrade to Pro to run two at once.',
      unlockCta: 'Unlock a case — €{P}',
      upsellCta: 'Upgrade to Pro',
    },
    nl: {
      eyebrow: 'JURIST OP VOLLE CAPACITEIT',
      titleSingular: 'Je jurist behandelt al jouw dossier.',
      titlePlural:   'Je jurist behandelt al {N} dossiers.',
      body: 'Een dossier komt vrij zodra het gesloten is. Ontgrendel er op aanvraag een extra, of upgrade naar Pro voor twee tegelijk.',
      unlockCta: 'Een dossier ontgrendelen — €{P}',
      upsellCta: 'Upgraden naar Pro',
    },
  };
  const juristCopyBase = JURIST_COPY[lang] || JURIST_COPY.fr;
  const juristTitle = (activeCount <= 1 ? juristCopyBase.titleSingular : juristCopyBase.titlePlural)
    .replace('{N}', String(activeCount));
  const juristCopy = {
    ...juristCopyBase,
    title: juristTitle,
    unlockCta: juristCopyBase.unlockCta.replace('{P}', String(juristPriceEur)),
  };

  // Écran LETTRE — écran de compatibilité, plus un écran de vente.
  //
  // La lettre n'a plus de quota mensuel : elle est une prestation du
  // dossier juriste ouvert, et il n'y a donc plus rien à débloquer à
  // 4,99 €. Ce bloc ne subsiste que pour un 402 hérité, parti d'un
  // onglet resté ouvert pendant le déploiement : il explique où se
  // trouve désormais l'envoi, sans annoncer un prix qui n'existe plus et
  // sans bouton d'achat que le serveur refuserait.
  const LETTER_COPY = {
    fr: {
      eyebrow: 'ENVOI PAR ARCHER',
      title:   'C’est ton juriste qui envoie ta lettre.',
      body:    'Ouvre ton dossier juriste sur cette affaire : il la relit, la signe et l’envoie, autant de fois que le dossier le demande.',
      upsellCta: 'Voir les formules',
    },
    en: {
      eyebrow: 'SENT BY ARCHER',
      title:   'Your jurist is the one who sends your letter.',
      body:    'Open your jurist case on this matter: they review it, sign it and send it, as many times as the case requires.',
      upsellCta: 'See the plans',
    },
    nl: {
      eyebrow: 'VERSTUURD DOOR ARCHER',
      title:   'Je jurist is degene die je brief verstuurt.',
      body:    'Open je juristendossier voor deze zaak: hij leest hem na, ondertekent en verstuurt hem, zo vaak als het dossier vraagt.',
      upsellCta: 'Bekijk de formules',
    },
  };
  const letterCopy = LETTER_COPY[lang] || LETTER_COPY.fr;

  const goToUpload = () => {
    onClose && onClose();
    // /upload est l'entrée où l'on dépose un document pour lancer une
    // analyse complète — les deux du mois sont comprises au Free.
    navigate('/upload');
  };

  const handleUnlock = async () => {
    // Une seule unité s'achète à l'écran de quota : l'analyse complète.
    // Le dépassement « lettre » a disparu avec le quota de lettres, et le
    // serveur refuse désormais cette unité au checkout — la demander ici
    // ouvrirait un paiement qui échoue.
    const unitType = 'analysis';
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/units/overage-checkout`,
        // Le backend refuse la session sans ce drapeau (400
        // consent_execution_required) ; le bouton qui appelle est
        // désactivé tant que la case n'est pas cochée.
        //
        // `case_id` et `return_path` ne voyagent QUE si l'achat part d'un
        // dossier, et c'est la distinction qui compte : sans dossier —
        // l'achat lancé depuis `/plans`, par exemple — il n'y a rien où
        // revenir, et le repli serveur (`/dashboard`) reste le bon.
        //
        // `case_id` sert à l'écran post-paiement, qui lit les métadonnées
        // de la session. `return_path` sert AVANT lui : c'est l'adresse
        // que Stripe reçoit, donc celle qui compte drapeau FERMÉ. Sans
        // lui, quelqu'un qui débloque une analyse depuis son dossier était
        // recraché sur le tableau de bord et devait retrouver son dossier
        // à la main.
        {
          unit_type: unitType,
          consent_execution: true,
          ...(caseId ? { case_id: caseId, return_path: returnPath() } : {}),
        },
        { withCredentials: true },
      );
      if (res.data && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      setError(t('quota.stripe_unavailable'));
    } catch (e) {
      const msg = (e && e.response && e.response.data && e.response.data.detail)
        || t('quota.checkout_unavailable');
      setError(typeof msg === 'string' ? msg : t('quota.checkout_unavailable'));
    } finally {
      setLoading(false);
    }
  };

  // Jurist-case one-shot unlock (49 €). Same endpoint as the 3 € analysis
  // unlock — the backend switches on `unit_type` in the request body.
  const handleUnlockJurist = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/units/overage-checkout`,
        {
          unit_type: 'jurist_case',
          consent_execution: true,
          ...(caseId ? { case_id: caseId, return_path: returnPath() } : {}),
        },
        { withCredentials: true },
      );
      if (res.data && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      setError(t('quota.stripe_unavailable'));
    } catch (e) {
      const msg = (e && e.response && e.response.data && e.response.data.detail)
        || t('quota.checkout_unavailable');
      setError(typeof msg === 'string' ? msg : t('quota.checkout_unavailable'));
    } finally {
      setLoading(false);
    }
  };

  const goToPlans = () => {
    onClose && onClose();
    navigate('/plans');
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="quota-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(10, 10, 15, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !loading) onClose && onClose(); }}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: 18,
          border: '1px solid #e2e0db',
          width: '100%',
          maxWidth: 460,
          // La modale liste jusqu'à quatre issues (payer le dépassement,
          // passer Pro, débloquer le juriste, voir les plans) : sur un
          // écran de téléphone en paysage, ou avec le clavier ouvert,
          // elle dépassait la hauteur disponible et le dernier bouton
          // devenait inatteignable. Elle défile désormais dans son cadre.
          maxHeight: 'calc(100dvh - 40px)',
          overflowY: 'auto',
          padding: '28px 28px 22px',
          position: 'relative',
          boxShadow: '0 30px 60px -20px rgba(15, 23, 42, 0.35)',
          fontFamily: 'var(--font-app)',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          aria-label={t('quota.close')}
          className="app-modal-close"
          style={{
            position: 'absolute', top: 12, right: 12,
            width: 32, height: 32, borderRadius: '50%',
            background: '#f4f4f1', border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#6b7280', cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          <X size={16} aria-hidden />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          {isAnalysis
            ? <Zap size={18} aria-hidden style={{ color: '#1a56db' }} />
            : <Lock size={18} aria-hidden style={{ color: '#1a56db' }} />}
          <div style={{ fontSize: 11, fontWeight: 800, color: '#1a56db', letterSpacing: 1 }}>
            {isComingSoon
              ? csCopy.eyebrow
              : (isJuristQuota
                  ? juristCopy.eyebrow
                  : (isUpgrade
                      ? featureCopy.eyebrow
                      : (isLetter
                          ? letterCopy.eyebrow
                          : (isAnalysis ? t('quota.eyebrow_analysis') : t('quota.eyebrow_quota')))))}
          </div>
        </div>

        <h2 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 800, color: '#0a0a0f', lineHeight: 1.35 }}>
          {isComingSoon
            ? csCopy.title
            : (isJuristQuota
                ? juristCopy.title
                : (isUpgrade
                    ? featureCopy.title
                    : (isLetter
                        ? letterCopy.title
                        : (isAnalysis
                            ? t('quota.title_analysis', { count: allowedLabel })
                            // Repli sans chiffre. Le backend ne lève plus
                            // qu'un seul quota_exceeded — `unit: "analysis"` —
                            // depuis la suppression du scan léger ; cette
                            // branche ne sert qu'à un 402 hérité d'un onglet
                            // resté ouvert pendant le déploiement.
                            : t('quota.title_quota')))))}
        </h2>

        <p style={{ margin: '0 0 20px', fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
          {isComingSoon
            ? csCopy.body
            : (isJuristQuota
                ? juristCopy.body
                : (isUpgrade
                    ? featureCopy.body
                    : (isLetter
                        ? letterCopy.body
                        : (isAnalysis
                            ? t('quota.body_analysis', { price: priceEur })
                            : t('quota.body_quota')))))}
        </p>

        {error && (
          <div
            style={{
              margin: '0 0 12px', padding: '10px 12px',
              background: '#fef2f2', border: '1px solid #fca5a5',
              borderRadius: 8, fontSize: 12, color: '#991b1b',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {isComingSoon ? (
            /* Pre-launch waiting screen — single CTA routes to the
               upload page. No à-la-carte purchase, no "Voir les plans"
               (we're already on /plans and the button would loop back). */
            <button
              type="button"
              onClick={goToUpload}
              data-testid="quota-modal-coming-soon-upload"
              style={{
                width: '100%', padding: '12px 18px',
                background: '#1a56db', color: '#ffffff',
                border: 'none', borderRadius: 10,
                fontSize: 14, fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {csCopy.cta}
            </button>
          ) : isJuristQuota ? (
            /* Jurist capacity — primary 49 € unlock, secondary /plans
               only when the caller is on Starter (Pro already has the
               max plan-included slots and shouldn't be upsold further). */
            <>
              {/* Case de rétractation — au-dessus du SEUL bouton qui
                  déclenche un paiement. « Passer au plan supérieur »
                  n'en est pas un : il mène à /plans, où la même case
                  attend dans l'étape de confirmation, après le choix de
                  la formule. Cette modale EST déjà une confirmation :
                  la case vit dedans plutôt que d'en empiler une seconde. */}
              <ConsentExecutionCheckbox
                id="consent-execution-jurist"
                testId="consent-execution-jurist"
                checked={consent}
                onChange={setConsent}
                disabled={loading}
              />
              <button
                type="button"
                onClick={handleUnlockJurist}
                disabled={loading || !consent}
                data-testid="quota-modal-jurist-unlock"
                style={{
                  width: '100%', padding: '12px 18px',
                  background: '#1a56db', color: '#ffffff',
                  border: 'none', borderRadius: 10,
                  fontSize: 14, fontWeight: 700,
                  cursor: (loading || !consent) ? 'not-allowed' : 'pointer',
                  opacity: (loading || !consent) ? 0.5 : 1,
                }}
              >
                {loading ? t('quota.redirecting') : juristCopy.unlockCta}
              </button>
              {showProUpsell && (
                <button
                  type="button"
                  onClick={goToPlans}
                  disabled={loading}
                  data-testid="quota-modal-jurist-upgrade"
                  style={{
                    width: '100%', padding: '12px 18px',
                    background: '#ffffff', color: '#0a0a0f',
                    border: '1px solid #d1d5db', borderRadius: 10,
                    fontSize: 14, fontWeight: 700,
                    cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {juristCopy.upsellCta}
                </button>
              )}
            </>
          ) : (
            <>
              {/* Analyse / lettre : dépassement avec bouton de déblocage
                  à l'unité (3 €). Le bouton "Passer au plan
                  supérieur" reste en secondaire. */}
              {isAnalysis && !isUpgrade && (
                <>
                  <ConsentExecutionCheckbox
                    id="consent-execution-unit"
                    testId="consent-execution-unit"
                    checked={consent}
                    onChange={setConsent}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={handleUnlock}
                    disabled={loading || !consent}
                    data-testid={isLetter ? 'quota-modal-letter-unlock' : 'quota-modal-unlock'}
                    style={{
                      width: '100%', padding: '12px 18px',
                      background: '#1a56db', color: '#ffffff',
                      border: 'none', borderRadius: 10,
                      fontSize: 14, fontWeight: 700,
                      cursor: (loading || !consent) ? 'not-allowed' : 'pointer',
                      opacity: (loading || !consent) ? 0.5 : 1,
                    }}
                  >
                    {loading
                      ? t('quota.redirecting')
                      : t('quota.unlock', { price: priceEur })}
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={goToPlans}
                disabled={loading}
                data-testid="quota-modal-plans"
                style={{
                  width: '100%', padding: '12px 18px',
                  // La variante ANALYSE garde le style secondaire : le
                  // bouton de déblocage est déjà au-dessus. Les autres
                  // n'ont rien à acheter, leur bouton devient le
                  // principal.
                  background: (isAnalysis && !isUpgrade) ? '#ffffff' : '#1a56db',
                  color:      (isAnalysis && !isUpgrade) ? '#0a0a0f' : '#ffffff',
                  border:     (isAnalysis && !isUpgrade) ? '1px solid #d1d5db' : 'none',
                  borderRadius: 10,
                  fontSize: 14, fontWeight: 700,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {(isAnalysis && !isUpgrade)
                  ? t('quota.upsell')
                  : t('quota.see_plans')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
